import {
    ClassicEditor,
    AccessibilityHelp,
    Alignment,
    Autoformat,
    AutoImage,
    Autosave,
    BlockQuote,
    Bold,
    CloudServices,
    Essentials,
    Heading,
    ImageBlock,
    ImageInline,
    ImageInsertViaUrl,
    ImageResize,
    ImageStyle,
    ImageTextAlternative,
    ImageToolbar,
    ImageUpload,
    Indent,
    IndentBlock,
    Italic,
    Link,
    List,
    ListProperties,
    MediaEmbed,
    Paragraph,
    PasteFromOffice,
    RemoveFormat,
    SelectAll,
    SourceEditing,
    Strikethrough,
    Subscript,
    Superscript,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar,
    TextTransformation,
    TodoList,
    Underline,
    Undo,
    Plugin, ButtonView,
    icons
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';

const buildQuotesRegExp = (quoteCharacter) => {
    return new RegExp(`(^|\\s)(${quoteCharacter})([^${quoteCharacter}]*)(${quoteCharacter})$`);
}

class Uppercase extends Plugin {
    static get pluginName() {
        return 'Uppercase';
    }

    init() {
        const editor = this.editor;

        editor.ui.componentFactory.add('uppercase', () => {
            const button = new ButtonView();

            button.set({
                label: 'Uppercase',
                icon: '<svg width="1200pt" height="1200pt" version="1.1" viewBox="0 0 1200 1200" xmlns="http://www.w3.org/2000/svg">\n' +
                    ' <path d="m320.15 691.59h180.84l-88.969-295.11zm9.1836-466.59h168.27l252.4 750h-161.5l-45.938-154.17h-262.55l-49.32 154.17h-155.7z" fill-rule="evenodd"/>\n' +
                    ' <path d="m1125 600-225-225-225 225h150v225h150v-225z" fill-rule="evenodd"/>\n' +
                    '</svg>\n',
                withText: false
            });

            button.on('execute', () => {
                const range = editor.model.document.selection.getFirstRange();

                let modifiedText = '';
                for (const item of range.getItems()) {
                    modifiedText += item.data.toUpperCase(); //return the selected text
                }

                editor.model.change(writer => {
                    editor.model.insertContent(writer.createText(modifiedText), range);
                });
            });

            return button;
        });
    }
}

const editorConfig = {
    toolbar: {
        items: [
            'undo',
            'redo',
            '|',
            'sourceEditing',
            '|',
            'heading',
            '|',
            'bold',
            'italic',
            'underline',
            'strikethrough',
            'subscript',
            'superscript',
            'removeFormat',
            '|',
            'link',
            'mediaEmbed',
            'insertTable',
            'blockQuote',
            '|',
            'alignment',
            '|',
            'bulletedList',
            'numberedList',
            'todoList',
            'outdent',
            'indent',
            '|',
            'uppercase',
        ],
        shouldNotGroupWhenFull: true
    },
    plugins: [
        AccessibilityHelp,
        Alignment,
        Autoformat,
        AutoImage,
        Autosave,
        BlockQuote,
        Bold,
        CloudServices,
        Essentials,
        Heading,
        ImageBlock,
        ImageInline,
        ImageInsertViaUrl,
        ImageResize,
        ImageStyle,
        ImageTextAlternative,
        ImageToolbar,
        ImageUpload,
        Indent,
        IndentBlock,
        Italic,
        Link,
        List,
        ListProperties,
        MediaEmbed,
        Paragraph,
        PasteFromOffice,
        RemoveFormat,
        SelectAll,
        SourceEditing,
        Strikethrough,
        Subscript,
        Superscript,
        Table,
        TableCaption,
        TableCellProperties,
        TableColumnResize,
        TableProperties,
        TableToolbar,
        TextTransformation,
        TodoList,
        Underline,
        Undo,
        Uppercase,
    ],
    heading: {
        options: [
            {
                model: 'paragraph',
                title: 'Paragraph',
                class: 'ck-heading_paragraph'
            },
            {
                model: 'heading1',
                view: 'h1',
                title: 'Heading 1',
                class: 'ck-heading_heading1'
            },
            {
                model: 'heading2',
                view: 'h2',
                title: 'Heading 2',
                class: 'ck-heading_heading2'
            },
            {
                model: 'heading3',
                view: 'h3',
                title: 'Heading 3',
                class: 'ck-heading_heading3'
            },
            {
                model: 'heading4',
                view: 'h4',
                title: 'Heading 4',
                class: 'ck-heading_heading4'
            },
            {
                model: 'heading5',
                view: 'h5',
                title: 'Heading 5',
                class: 'ck-heading_heading5'
            },
            {
                model: 'heading6',
                view: 'h6',
                title: 'Heading 6',
                class: 'ck-heading_heading6'
            }
        ]
    },
    image: {
        toolbar: ['imageTextAlternative', '|', 'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', '|', 'resizeImage']
    },
    link: {
        addTargetToExternalLinks: true,
        defaultProtocol: 'https://',
        decorators: {
            toggleDownloadable: {
                mode: 'manual',
                label: 'Downloadable',
                attributes: {
                    download: 'file'
                }
            }
        }
    },
    list: {
        properties: {
            styles: true,
            startIndex: true,
            reversed: true
        }
    },
    typing: {
        transformations: {
            remove: [
                'quotes',
                'quotesPrimaryPl',
            ]
        }
    },
    placeholder: 'Type or paste your content here!',
    table: {
        contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
    }
};

const quotes = {
    lt: {
        from: buildQuotesRegExp('"'),
        to: [
            null,
            '„',
            null,
            '“'
        ]
    },
    en: {
        from: buildQuotesRegExp('"'),
        to: [
            null,
            '“',
            null,
            '”'
        ]
    },
}

window.ckEditorInstances = {};

window.loadEditor = function (name, locale, isEmployee) {

    const textarea = document.querySelector('#' + name);

    if (!isEmployee) {
        editorConfig.toolbar.items = editorConfig.toolbar.items.filter(item => item !== 'mediaEmbed');
    }

    if (locale === '0') {
        editorConfig.typing.transformations.extra = [quotes.lt];
    } else {
        editorConfig.typing.transformations.extra = [quotes.en];
    }

    ClassicEditor
        .create(textarea, editorConfig)
        .then((newEditor) => {

            window.ckEditorInstances[name] = {
                editor: newEditor,
                name: name,
                isEmployee: isEmployee
            };

            newEditor.model.document.on('change:data', () => {
                textarea.value = newEditor.getData();
            });
        });
}

window.reloadEditors = function (locale) {

    const editors = Object.values(window.ckEditorInstances);

    editors.forEach(editor => {
        editor.editor.destroy();
        loadEditor(editor.name, locale, editor.isEmployee);
    });

}




